import { useState, useMemo } from "react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Pencil, Trash2, Ruler, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, ArrowUp, ArrowDown } from "lucide-react";

interface Anthropometry {
  type: "anthropometry";
  date: string;
  is_main_record: boolean;
  name?: string | null; // Nome do documento/laudo (apenas para is_main_record)
  measurement_type?: string | null; // Coluna 2: Dado
  value?: number | null; // Coluna 3: Medida
  unit?: string | null; // Coluna 4: Unidade
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileAnthropometryDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  onRecordsChange?: (updatedRecords: any[]) => void;
  expandAllByDefault?: boolean;
}

interface EditableAnthropometry extends Anthropometry {
  _index?: number;
}

export function SmartfileAnthropometryDetails({ session, onRefresh, onRecordsChange, expandAllByDefault = false }: SmartfileAnthropometryDetailsProps) {
  const isMobile = useIsMobile();

  // Buscar o registro principal (is_main_record: true) - memoizado
  const mainRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'anthropometry' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
    ) as Anthropometry | undefined;
  }, [session.structured_records]);

  // Filtrar apenas anthropometry (excluir registro principal) - memoizado
  const anthropometryRecords: EditableAnthropometry[] = useMemo(() => {
    return (session.structured_records || [])
      .filter((record: any): record is Anthropometry =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'anthropometry' &&
        !(record as any).is_main_record // Excluir registro principal
      )
      .map((record: Anthropometry, index: number) => ({
        ...record,
        _index: index
      }));
  }, [session.structured_records]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingMainRecord, setEditingMainRecord] = useState(false);
  const [editedRecords, setEditedRecords] = useState<EditableAnthropometry[]>(anthropometryRecords);
  const [editedMainRecord, setEditedMainRecord] = useState<Anthropometry | undefined>(mainRecord);
  const [isSaving, setIsSaving] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroups, setExpandedDateGroups] = useState<Map<string, boolean>>(new Map());

  // Atualizar editedRecords quando anthropometryRecords mudar
  React.useEffect(() => {
    setEditedRecords(anthropometryRecords);
    setEditedMainRecord(mainRecord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.structured_records]);

  const handleSave = async (index: number) => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real no array completo
      let anthropometryCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'anthropometry' &&
          !(record as any).is_main_record) {
          if (anthropometryCount === index) {
            realIndex = i;
            break;
          }
          anthropometryCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro atualizado com sucesso!");
        setEditingIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let anthropometryCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'anthropometry' &&
          !(record as any).is_main_record) {
          if (anthropometryCount === index) {
            realIndex = i;
            break;
          }
          anthropometryCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancel = () => {
    setEditedRecords(anthropometryRecords);
    setEditingIndex(null);
  };

  const handleSaveMainRecord = async () => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice do registro principal
      const mainRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'anthropometry' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
      );

      if (mainRecordIndex !== -1 && editedMainRecord) {
        updatedRecords[mainRecordIndex] = editedMainRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Nome do documento atualizado com sucesso!");
        setEditingMainRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelMainRecord = () => {
    setEditedMainRecord(mainRecord);
    setEditingMainRecord(false);
  };

  if (anthropometryRecords.length === 0 && !mainRecord) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <p className="text-sm">Nenhuma medida antropométrica encontrada nesta sessão</p>
      </div>
    );
  }

  // Agrupar por mês/ano
  const groupedByDate = anthropometryRecords.reduce((acc, record, index) => {
    const date = record.date || session.created_at;
    const dateObj = new Date(date);

    // Validar se a data é válida
    if (isNaN(dateObj.getTime())) {
      // Se a data for inválida, usar uma chave padrão
      const invalidKey = 'invalid-date';
      if (!acc[invalidKey]) {
        acc[invalidKey] = {
          displayKey: 'Data Inválida',
          records: []
        };
      }
      acc[invalidKey].records.push({ ...record, _index: index });
      return acc;
    }

    const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

    if (!acc[monthYearKey]) {
      acc[monthYearKey] = {
        displayKey,
        records: []
      };
    }
    acc[monthYearKey].records.push({ ...record, _index: index });
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditableAnthropometry[] }>);

  // Ordenar por mês/ano conforme o estado de ordenação
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime();
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Toggle de expansão de grupos de data
  const toggleDateGroup = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroups((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  // Toggle de ordenação de data
  const toggleDateSort = () => {
    setDateSortOrder((prev) => prev === 'asc' ? 'desc' : 'asc');
  };

  // Função para renderizar uma linha de registro
  const renderRecordRow = (record: EditableAnthropometry, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingIndex === originalIndex;

    const measurementTypeLabels: Record<string, string> = {
      peso: 'Peso',
      altura: 'Altura',
      circunferencia_cintura: 'Circunferência da Cintura',
      circunferencia_quadril: 'Circunferência do Quadril',
      imc: 'IMC',
      gordura_corporal: 'Gordura Corporal'
    };

    return (
      <React.Fragment key={`${dateKey}-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={5} className="p-3 bg-muted/30">
              <div className="space-y-3">
                {/* Ordem: Data, Dado, Medida, Unidade */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      value={editedRecords[originalIndex]?.date || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          date: e.target.value
                        };
                        setEditedRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Dado</Label>
                    <Input
                      value={editedRecords[originalIndex]?.measurement_type || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          measurement_type: e.target.value || null
                        };
                        setEditedRecords(updated);
                      }}
                      placeholder="Ex: Peso, Altura"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Medida</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedRecords[originalIndex]?.value ?? ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          value: e.target.value ? parseFloat(e.target.value) : null
                        };
                        setEditedRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unidade</Label>
                    <Input
                      value={editedRecords[originalIndex]?.unit || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          unit: e.target.value || null
                        };
                        setEditedRecords(updated);
                      }}
                      placeholder="kg, cm, m, %"
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSave(originalIndex)} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            {/* Ordem: Data, Dado, Medida, Unidade */}
            <td className="p-3 text-sm text-muted-foreground w-[120px]">
              <div className="truncate">
                {record.date && !isNaN(new Date(record.date).getTime())
                  ? format(new Date(record.date), "dd/MM/yyyy")
                  : '–'}
              </div>
            </td>
            <td className="p-3 w-[200px]">
              <div className="text-sm font-medium truncate">
                {measurementTypeLabels[record.measurement_type || ''] || record.measurement_type || '–'}
              </div>
            </td>
            <td className="p-3 w-[120px]">
              <div className="text-sm truncate">
                {record.value !== undefined && record.value !== null ? record.value : '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.unit || '–'}
              </div>
            </td>
            <td className="p-3 text-right w-[120px]">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir esta medida antropométrica?
                        <br />
                        <span className="font-medium mt-2 block">
                          {record.date && !isNaN(new Date(record.date).getTime())
                            ? format(new Date(record.date), "dd/MM/yyyy")
                            : 'Data inválida'} - {measurementTypeLabels[record.measurement_type || ''] || record.measurement_type || 'Medida'}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar card mobile
  const renderMobileCard = (record: EditableAnthropometry, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingIndex === originalIndex;

    const measurementTypeLabels: Record<string, string> = {
      peso: 'Peso',
      altura: 'Altura',
      circunferencia_cintura: 'Circunferência da Cintura',
      circunferencia_quadril: 'Circunferência do Quadril',
      imc: 'IMC',
      gordura_corporal: 'Gordura Corporal'
    };

    return (
      <Card key={`${dateKey}-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={editedRecords[originalIndex]?.date || ''}
                  onChange={(e) => {
                    const updated = [...editedRecords];
                    updated[originalIndex] = { ...updated[originalIndex], date: e.target.value };
                    setEditedRecords(updated);
                  }}
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Dado</Label>
                  <Input
                    value={editedRecords[originalIndex]?.measurement_type || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = { ...updated[originalIndex], measurement_type: e.target.value || null };
                      setEditedRecords(updated);
                    }}
                    placeholder="Ex: Peso, Altura"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Medida</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedRecords[originalIndex]?.value ?? ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = { ...updated[originalIndex], value: e.target.value ? parseFloat(e.target.value) : null };
                      setEditedRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Unidade</Label>
                <Input
                  value={editedRecords[originalIndex]?.unit || ''}
                  onChange={(e) => {
                    const updated = [...editedRecords];
                    updated[originalIndex] = { ...updated[originalIndex], unit: e.target.value || null };
                    setEditedRecords(updated);
                  }}
                  placeholder="kg, cm, m, %"
                  className="h-9"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSave(originalIndex)} disabled={isSaving} className="flex-1">
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Cabeçalho: Título, Data e Botões de Ação */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Ruler className="h-4 w-4 text-muted-foreground" />
                    <h4 className="font-semibold text-sm leading-tight">
                      {measurementTypeLabels[record.measurement_type || ''] || record.measurement_type || 'Medida'}
                    </h4>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {record.date && !isNaN(new Date(record.date).getTime())
                      ? format(new Date(record.date), "dd/MM/yyyy")
                      : '–'}
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingIndex(originalIndex)}
                    className="h-8 px-2 text-xs"
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Excluir
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir esta medida antropométrica?
                          <br />
                          <span className="font-medium mt-2 block">
                            {record.date && !isNaN(new Date(record.date).getTime())
                              ? format(new Date(record.date), "dd/MM/yyyy")
                              : 'Data inválida'} - {measurementTypeLabels[record.measurement_type || ''] || record.measurement_type || 'Medida'}
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(originalIndex)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Duas colunas: Valor, Unidade */}
              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border">
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Valor</span>
                  <span className="text-sm font-medium">{record.value ?? '–'}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Unidade</span>
                  <span className="text-sm font-medium">{record.unit || '–'}</span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="bg-background border-t">
      {/* Seção: Título Editável (sem cabeçalho "Informações do Documento") */}
      {mainRecord && (
        <div className="mb-4 border-b border-border pb-4 px-4 pt-4">
          {editingMainRecord ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nome do Documento</Label>
                <Input
                  value={editedMainRecord?.name || ''}
                  onChange={(e) => {
                    setEditedMainRecord({
                      ...editedMainRecord!,
                      name: e.target.value || null
                    });
                  }}
                  placeholder="Ex: Antropometria - 28/11/2025"
                  className="h-8"
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveMainRecord} disabled={isSaving}>
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelMainRecord} disabled={isSaving}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs text-muted-foreground">Nome do Documento</Label>
                <p className="text-sm font-medium mt-1">
                  {mainRecord.name || 'Sem nome'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingMainRecord(true)}
                className="h-7 px-2"
              >
                <Pencil className="h-3 w-3 mr-1" />
                Editar
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Seção: Antropometria */}
      {anthropometryRecords.length > 0 && (
        <div>
          {/* Mobile: Cards View */}
          {isMobile ? (
            <div className="p-3">
              {sortedDates.map((monthYearKey) => {
                const dateGroup = groupedByDate[monthYearKey];
                const dateRecords = dateGroup.records;
                const displayKey = dateGroup.displayKey;
                const defaultExpanded = expandAllByDefault;
                const manualState = expandedDateGroups.get(monthYearKey);
                const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                return (
                  <div key={monthYearKey} className="mb-4">
                    <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(monthYearKey, open)}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer bg-blue-500/5 hover:bg-blue-500/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <Ruler className="h-4 w-4" />
                          <span>📅 {displayKey}</span>
                          <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                          <Badge variant="secondary">
                            {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {dateRecords.map((record, groupIndex) => renderMobileCard(record, groupIndex, monthYearKey))}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop: Table View */
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Data
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Dado
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Medida
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Unidade
                    </th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDates.map((monthYearKey) => {
                    const dateGroup = groupedByDate[monthYearKey];
                    const dateRecords = dateGroup.records;
                    const displayKey = dateGroup.displayKey;
                    const defaultExpanded = expandAllByDefault;
                    const manualState = expandedDateGroups.get(monthYearKey);
                    const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                    return (
                      <React.Fragment key={monthYearKey}>
                        <tr className="bg-blue-500/5 border-y-2 border-blue-500/20">
                          <td colSpan={5} className="p-2">
                            <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(monthYearKey, open)}>
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer hover:bg-blue-500/10 rounded px-2 py-1 transition-colors w-full">
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  <Ruler className="h-4 w-4" />
                                  <span>📅 {displayKey}</span>
                                  <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                  <Badge variant="secondary">
                                    {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                              </CollapsibleTrigger>
                            </Collapsible>
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            {dateRecords.map((record, groupIndex) => renderRecordRow(record, groupIndex, monthYearKey))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
            Total: {anthropometryRecords.length} registro(s) de antropometria em {sortedDates.length} mês(es)/ano(s)
          </div>
        </div>
      )}
    </div>
  );
}

